import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Card, Modal, Form, InputNumber, Select, message, Tooltip, Tag } from 'antd';
import { PlusOutlined, ReloadOutlined, ProfileOutlined, BankOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { payrollApi, employeesApi, bankAccountsApi, extractApiUserMessage } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatMoneyFa } from '../../utils/formatPriceToman';
import { toJalali } from '../../utils/dateUtils';

const { Option } = Select;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const PERSIAN_MONTHS = [
  { value: 1, label: 'فروردین' },
  { value: 2, label: 'اردیبهشت' },
  { value: 3, label: 'خرداد' },
  { value: 4, label: 'تیر' },
  { value: 5, label: 'مرداد' },
  { value: 6, label: 'شهریور' },
  { value: 7, label: 'مهر' },
  { value: 8, label: 'آبان' },
  { value: 9, label: 'آذر' },
  { value: 10, label: 'دی' },
  { value: 11, label: 'بهمن' },
  { value: 12, label: 'اسفند' },
];

const getYearsList = () => {
  let curYear = 1405;
  try {
    const y = toJalali(new Date(), 'jYYYY');
    if (y) curYear = parseInt(y, 10);
  } catch (e) {}
  const years = [];
  for (let y = curYear - 3; y <= curYear + 3; y++) {
    years.push(y);
  }
  return years;
};

const amountToPersianWords = (n) => {
  if (!n || isNaN(n)) return '';
  n = Math.floor(Number(n));
  if (n === 0) return 'صفر تومان';
  const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
  const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
  const tens = ['', 'ده', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
  const hundreds = ['', 'یکصد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
  const scales = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'];

  function chunkToWords(num) {
    let parts = [];
    const h = Math.floor(num / 100);
    const rem = num % 100;
    if (h > 0) parts.push(hundreds[h]);
    if (rem >= 10 && rem <= 19) {
      parts.push(teens[rem - 10]);
    } else {
      const t = Math.floor(rem / 10);
      const u = rem % 10;
      if (t > 0) parts.push(tens[t]);
      if (u > 0) parts.push(units[u]);
    }
    return parts.join(' و ');
  }

  let chunks = [];
  let temp = n;
  while (temp > 0) {
    chunks.push(temp % 1000);
    temp = Math.floor(temp / 1000);
  }

  let words = [];
  for (let i = chunks.length - 1; i >= 0; i--) {
    const chunk = chunks[i];
    if (chunk > 0) {
      const chunkWord = chunkToWords(chunk);
      const scaleWord = scales[i];
      words.push(scaleWord ? `${chunkWord} ${scaleWord}` : chunkWord);
    }
  }

  return words.join(' و ') + ' تومان';
};

const Payroll = () => {
  const [loading, setLoading] = useState(false);
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPayModalVisible, setIsPayModalVisible] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [baseSalary, setBaseSalary] = useState(null);
  const [form] = Form.useForm();
  const [payForm] = Form.useForm();

  const yearsList = getYearsList();

  const fetchDependencies = async () => {
    try {
      const [empRes, bankRes] = await Promise.all([
        employeesApi.getAll().catch(() => null),
        bankAccountsApi.getAll().catch(() => null),
      ]);
      if (empRes?.data) setEmployees(empRes.data);
      else if (Array.isArray(empRes)) setEmployees(empRes);

      let accounts = [];
      if (bankRes?.data?.bank_accounts) accounts = bankRes.data.bank_accounts;
      else if (bankRes?.bank_accounts) accounts = bankRes.bank_accounts;
      else if (bankRes?.data?.accounts) accounts = bankRes.data.accounts;
      else if (bankRes?.accounts) accounts = bankRes.accounts;
      else if (bankRes?.banks) accounts = bankRes.banks;
      setBankAccounts(Array.isArray(accounts) ? accounts : []);
    } catch (error) {
      console.error('Error fetching dependencies:', error);
    }
  };

  const fetchPayrolls = async () => {
    setLoading(true);
    try {
      const res = await payrollApi.getAll();
      const data = res?.data?.payrolls || res?.payrolls || res?.data || [];
      setPayrolls(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching payrolls:', error);
      setPayrolls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDependencies();
    fetchPayrolls();
  }, []);

  const handleOpenModal = () => {
    let curYear = 1405;
    let curMonth = 7;
    try {
      const y = toJalali(new Date(), 'jYYYY');
      const m = toJalali(new Date(), 'jM');
      if (y) curYear = parseInt(y, 10);
      if (m) curMonth = parseInt(m, 10);
    } catch (e) {}

    form.setFieldsValue({
      period_year: curYear,
      period_month: curMonth,
      base_salary: undefined,
      overtime_amount: undefined,
      bonus_amount: undefined,
      deductions_amount: undefined,
    });
    setBaseSalary(null);
    setIsModalVisible(true);
  };

  const handleGenerate = async (values) => {
    setActionLoading(true);
    try {
      await payrollApi.generate({
        ...values,
        period_year: Number(values.period_year),
        period_month: Number(values.period_month),
        base_salary: Number(values.base_salary),
        overtime_amount: Number(values.overtime_amount || 0),
        bonus_amount: Number(values.bonus_amount || 0),
        deductions_amount: Number(values.deductions_amount || 0),
      });
      message.success('فیش حقوقی با موفقیت صادر شد');
      setIsModalVisible(false);
      form.resetFields();
      setBaseSalary(null);
      fetchPayrolls();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در صدور فیش حقوقی');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePay = async (values) => {
    if (!selectedPayroll) return;
    setActionLoading(true);
    try {
      await payrollApi.pay(selectedPayroll.id, {
        bank_account_id: values.bank_account_id,
      });
      message.success('فیش حقوقی پرداخت شد');
      setIsPayModalVisible(false);
      payForm.resetFields();
      fetchPayrolls();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در پرداخت فیش حقوقی');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>کارمند</span>,
      key: 'user',
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {record.user?.name || '-'}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>دوره</span>,
      key: 'period',
      render: (_, record) => {
        const monthObj = PERSIAN_MONTHS.find(m => m.value === Number(record.period_month));
        const monthText = monthObj ? monthObj.label : toPersianNumber(record.period_month);
        return (
          <span style={{ fontFamily: 'CustomFont' }}>
            {monthText} {toPersianNumber(record.period_year)}
          </span>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مبلغ قابل پرداخت</span>,
      dataIndex: 'net_payable',
      key: 'net_payable',
      render: (amount) => (
        <span className="text-blue-600 font-medium" style={{ fontFamily: 'CustomFont' }}>
          {formatMoneyFa(amount)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'paid' ? 'success' : 'warning'} style={{ fontFamily: 'CustomFont' }}>
          {status === 'paid' ? 'پرداخت شده' : 'در انتظار پرداخت'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {record.status !== 'paid' && (
            <Button
              type="primary"
              size="small"
              icon={<BankOutlined />}
              onClick={() => {
                setSelectedPayroll(record);
                setIsPayModalVisible(true);
              }}
              style={{ fontFamily: 'CustomFont', backgroundColor: '#52c41a' }}
            >
              پرداخت
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center">
                  <ProfileOutlined className="text-teal-600 dark:text-teal-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0" style={{ fontFamily: 'CustomFont' }}>
                    فیش حقوقی
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                    صدور و پرداخت فیش حقوقی پرسنل
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip title="بروزرسانی">
                  <Button icon={<ReloadOutlined />} onClick={fetchPayrolls} size="middle" loading={loading} />
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleOpenModal}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  صدور فیش جدید
                </Button>
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={4} />
            ) : (
              <Card className="dark:bg-gray-800 border-0 shadow-sm">
                <Table
                  columns={columns}
                  dataSource={payrolls}
                  rowKey="id"
                  pagination={false}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>صدور فیش حقوقی</span>}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setBaseSalary(null);
          form.resetFields();
        }}
        footer={null}
        width={550}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleGenerate}>
          <Form.Item
            name="user_id"
            label={<span style={{ fontFamily: 'CustomFont' }}>کارمند</span>}
            rules={[{ required: true, message: 'انتخاب کارمند الزامی است' }]}
          >
            <Select showSearch optionFilterProp="children" placeholder="انتخاب کارمند..." style={{ fontFamily: 'CustomFont' }}>
              {employees.map(e => <Option key={e.id} value={e.id}>{e.name}</Option>)}
            </Select>
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="period_year"
              label={<span style={{ fontFamily: 'CustomFont' }}>سال</span>}
              rules={[{ required: true, message: 'انتخاب سال الزامی است' }]}
            >
              <Select
                showSearch
                placeholder="انتخاب سال..."
                style={{ fontFamily: 'CustomFont' }}
              >
                {yearsList.map(y => (
                  <Option key={y} value={y}>
                    {toPersianNumber(y)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="period_month"
              label={<span style={{ fontFamily: 'CustomFont' }}>ماه</span>}
              rules={[{ required: true, message: 'انتخاب ماه الزامی است' }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                placeholder="انتخاب ماه..."
                style={{ fontFamily: 'CustomFont' }}
              >
                {PERSIAN_MONTHS.map(m => (
                  <Option key={m.value} value={m.value}>
                    {m.label} ({toPersianNumber(m.value)})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            name="base_salary"
            label={<span style={{ fontFamily: 'CustomFont' }}>حقوق پایه (تومان)</span>}
            rules={[{ required: true, message: 'وارد کردن حقوق پایه الزامی است' }]}
          >
            <InputNumber
              className="w-full font-mono text-base font-bold text-emerald-600"
              style={{ fontFamily: 'CustomFont' }}
              placeholder="مبلغ حقوق پایه به تومان"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              onChange={(val) => setBaseSalary(val)}
            />
          </Form.Item>
          {baseSalary && (
            <div className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-lg -mt-3 mb-4 border border-emerald-100 dark:border-emerald-900/50" style={{ fontFamily: 'CustomFont' }}>
              <span className="text-gray-400 ml-1">به حروف:</span>
              <span className="font-bold">{amountToPersianWords(baseSalary)}</span>
            </div>
          )}

          <Form.Item
            name="overtime_amount"
            label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ اضافه‌کاری (تومان)</span>}
          >
            <InputNumber
              className="w-full font-mono"
              style={{ fontFamily: 'CustomFont' }}
              placeholder="مبلغ اضافه‌کاری به تومان"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="bonus_amount"
            label={<span style={{ fontFamily: 'CustomFont' }}>سایر پاداش‌ها (تومان)</span>}
          >
            <InputNumber
              className="w-full font-mono"
              style={{ fontFamily: 'CustomFont' }}
              placeholder="سایر پاداش‌ها به تومان"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="deductions_amount"
            label={<span style={{ fontFamily: 'CustomFont' }}>کسورات (تومان)</span>}
          >
            <InputNumber
              className="w-full font-mono text-rose-600"
              style={{ fontFamily: 'CustomFont' }}
              placeholder="کسورات به تومان"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
          
          <div className="mb-4 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 p-2.5 rounded-lg border border-gray-100 dark:border-gray-700" style={{ fontFamily: 'CustomFont' }}>
            توجه: موتور خودکار مجموع پورسانت‌های تایید شده این ماه را به فیش اضافه کرده و مبلغ نهایی (net_payable) را محاسبه خواهد کرد.
          </div>

          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false);
                setBaseSalary(null);
                form.resetFields();
              }} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}>
                صدور فیش
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>پرداخت فیش حقوقی</span>}
        open={isPayModalVisible}
        onCancel={() => setIsPayModalVisible(false)}
        footer={null}
      >
        <Form form={payForm} layout="vertical" onFinish={handlePay}>
          <Form.Item name="bank_account_id" label={<span style={{ fontFamily: 'CustomFont' }}>حساب بانکی</span>} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" style={{ fontFamily: 'CustomFont' }}>
              {bankAccounts.map(b => <Option key={b.id} value={b.id}>{b.account_name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => setIsPayModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#52c41a' }}>
                ثبت پرداخت
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default Payroll;
